import type { CameraStatus, Site } from './types'
import type { CameraTamper, TamperSignal } from './api'
import type { SiteHealth } from './useCameraHealth'

/** Tampered cameras for every site, keyed site → camera. */
export type SiteTamper = Record<string, Record<string, CameraTamper>>

/** One tampered camera under its site's row. */
export interface TamperedCamera {
  camera: string
  since: string
  clusterId?: number
  signals?: TamperSignal[]
}

/**
 * One site's current problem, as the navbar indicator reports it.
 *
 * Grouped per site rather than per camera: a site losing forty cameras is one
 * fault to act on, and forty rows would bury every other site in the list.
 */
export interface SiteIssue {
  siteId: string
  siteName: string
  kind: 'cameras-tampered' | 'unreachable' | 'all-cameras-offline' | 'cameras-offline' | 'cameras-degraded'
  /** Empty where naming them adds nothing: `unreachable`, `all-cameras-offline`. */
  cameras: string[]
  /**
   * Degraded cameras this row does not already name.
   *
   * Only `cameras-offline` carries them. One row per site means the worse
   * state takes the kind, which dropped degradation from exactly the sites
   * where it matters most; `cameras-degraded` names them in `cameras`.
   */
  degraded?: string[]
  /**
   * Each tampered camera, when its view was lost and the incident it raised,
   * since a row's controls act on one camera. Only `cameras-tampered` has it.
   *
   * `clusterId` is absent where the state was raised before any incident was
   * stored, which the row renders as having nothing to open.
   */
  tampered?: TamperedCamera[]
  /**
   * Set when a tampered site is also unreachable.
   *
   * Ranking picks the kind, not everything the row may say. Without this the
   * outage is invisible until the tampering clears, which is backwards — a
   * site that is both is in more trouble, not less.
   */
  alsoUnreachable?: boolean
}

const urgency: SiteIssue['kind'][] = ['cameras-tampered', 'unreachable', 'all-cameras-offline', 'cameras-offline', 'cameras-degraded']

/**
 * The sites currently worth someone's attention, most urgent first.
 *
 * Only an explicit `offline` or `degraded` counts. `unknown` is the default — a
 * VMS that has just started, a Frigate camera still settling, and every
 * camera on a site whose kind has no health source. Reporting it would light
 * this permanently for a site that is fine.
 *
 * One row per site, so the kinds rank in `urgency`'s order, which also orders
 * the list.
 */
export function siteIssues(
  sites: Record<string, Site>,
  health: SiteHealth,
  unreachable: ReadonlySet<string>,
  tamper: SiteTamper = {},
): SiteIssue[] {
  const issues: SiteIssue[] = []

  for (const site of Object.values(sites)) {
    const name = site.name || site.id

    // Above every fault below it: those describe something broken, this
    // describes someone interfering. It is also the rarest, so ranking it
    // first costs the others nothing.
    const tampered = Object.keys(tamper[site.id] ?? {}).sort()
    if (tampered.length > 0) {
      issues.push({
        siteId: site.id,
        siteName: name,
        kind: 'cameras-tampered',
        cameras: tampered,
        tampered: tampered.map((camera) => {
          const { since, cluster_id, signals } = tamper[site.id][camera]
          return { camera, since, ...(cluster_id && { clusterId: cluster_id }), ...(signals && { signals }) }
        }),
        ...(unreachable.has(site.id) && { alsoUnreachable: true }),
      })
      continue
    }

    // Health for a site we cannot reach is whatever it last said, which is not
    // evidence about now. One fault, named for what is actually wrong.
    if (unreachable.has(site.id)) {
      issues.push({ siteId: site.id, siteName: name, kind: 'unreachable', cameras: [] })
      continue
    }

    const cameras = health[site.id] ?? {}
    const named = (want: CameraStatus) =>
      Object.entries(cameras).filter(([, status]) => status === want).map(([camera]) => camera).sort()

    const offline = named('offline')
    const degraded = named('degraded')

    if (offline.length > 0) {
      // `degraded` is still recording, so it counts as something left
      // standing. `unknown` does not — a site that has just dropped is full of
      // it, and waiting would print the long list during the minute it matters.
      const working = Object.values(cameras).some((s) => s === 'online' || s === 'degraded')
      if (working) {
        issues.push({
          siteId: site.id,
          siteName: name,
          kind: 'cameras-offline',
          cameras: offline,
          ...(degraded.length > 0 && { degraded }),
        })
      } else {
        issues.push({ siteId: site.id, siteName: name, kind: 'all-cameras-offline', cameras: [] })
      }
    } else if (degraded.length > 0) {
      issues.push({ siteId: site.id, siteName: name, kind: 'cameras-degraded', cameras: degraded })
    }
  }

  // Most urgent first, then by name: stable, so a list glanced at repeatedly
  // does not reshuffle as polls land underneath it.
  return issues.sort((a, b) => urgency.indexOf(a.kind) - urgency.indexOf(b.kind) || a.siteName.localeCompare(b.siteName))
}
